// BrandIndustryData.js
import React, { useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ActivityIndicator,
  ScrollView,
  TouchableOpacity,
  SafeAreaView,
  Image,
  Modal,
  Pressable,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { Linking } from 'react-native';

const BrandIndustryData = ({ navigation }) => {
  const [loading, setLoading] = useState(true);

  // data
  const [payload, setPayload] = useState(null);
  const [apiError, setApiError] = useState(null);

  // NEW: session + auth state for sidenav (same pattern)
  const [userData, setUserData] = useState({});
  const [isAuthed, setIsAuthed] = useState(false);
  const [authMode, setAuthMode] = useState('none'); // "subscriber" | "none"
  const [authChecked, setAuthChecked] = useState(false);
  const isSubscriber = authMode === 'subscriber';

  // AI
  const [aiLoading, setAiLoading] = useState(false);
  const [aiPlan, setAiPlan] = useState(null);
  const [aiErr, setAiErr] = useState(null);

  // Tooltip modal (always on top)
  const [tipOpen, setTipOpen] = useState(false);
  const [tipTitle, setTipTitle] = useState('');
  const [tipBody, setTipBody] = useState('');

  useEffect(() => {
    fetch('https://collabbayadmin.com/APIControllers/GetBrandAIDataAnalysis.php', {
      credentials: 'include',
    })
      .then((res) => res.json())
      .then((data) => {
        if (!data || data.ok === false) {
          setApiError(data?.error || 'Failed to load data');
          setPayload(null);
        } else {
          setPayload(data);
          setApiError(null);
        }
      })
      .catch((err) => {
        console.error(err);
        setApiError('Network error fetching industry data.');
        setPayload(null);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetch('https://collabbayadmin.com/APIControllers/GetSessionData.php', {
      credentials: 'include',
    })
      .then((response) => response.json())
      .then((data) => {
        setUserData(data || {});
      })
      .catch((error) => {
        console.error('Error fetching user data:', error);
      });
  }, []);

  // Subscription/auth state for sidenav
  useEffect(() => {
    fetch('https://collabbayadmin.com/APIControllers/UserSubscriptionAuth.php', {
      method: 'GET',
      credentials: 'include',
    })
      .then((res) => res.json())
      .then((data) => {
        const authed =
          typeof data.authenticated === 'boolean'
            ? data.authenticated
            : data.status === 'active';
        const mode = data.auth_mode
          ? data.auth_mode
          : data.status === 'active'
          ? 'subscriber'
          : 'none';

        setIsAuthed(!!authed);
        setAuthMode(mode);
      })
      .catch((err) => console.error('Error fetching auth state:', err))
      .finally(() => setAuthChecked(true));
  }, []);

  // ---- HELPERS ----
  const openInSameTab = (url) => {
    if (typeof window !== 'undefined') {
      window.location.href = url;
    } else {
      Linking.openURL(url);
    }
  };

  const showTip = (title, body) => {
    setTipTitle(title);
    setTipBody(body);
    setTipOpen(true);
  };

  const handleBackPress = () => {
    // match your style: “Back to activity”
    navigation.goBack();
  };

  const formatPct = (n) => {
    const x = Number(n);
    if (!isFinite(x)) return '0%';
    return `${x.toFixed(x % 1 === 0 ? 0 : 1)}%`;
  };

  const clamp = (n, a, b) => Math.max(a, Math.min(b, n));

  const percentileToRankText = (pct) => {
    if (pct === null || pct === undefined || !isFinite(Number(pct))) return 'N/A';
    const p = Number(pct);
    // p = percentile rank in [0..100] where higher means bigger vs market
    // user wants: "top X%" or "bottom X%"
    if (p >= 50) return `top ${Math.round(100 - p)}%`;
    return `bottom ${Math.round(p)}%`;
  };

  const percentileToPlain = (pct) => {
    if (pct === null || pct === undefined || !isFinite(Number(pct))) return 'N/A';
    const p = Number(pct);
    if (p >= 50) return `Top ${Math.round(100 - p)}%`;
    return `Bottom ${Math.round(p)}%`;
  };

  // -------------------------
  // DERIVED DATA (Market vs Your)
  // -------------------------
  const brandName = payload?.brand?.name || userData?.Username || 'Your venue';
  const category = payload?.brand?.category || '—';

  const listing = payload?.brand?.listing || null;

  const marketTotalRequests =
    payload?.industry?.distributions?.total_requests ??
    payload?.industry?.distributions?.total_bookings ??
    0;

  const yourCredits = Number(payload?.comparison?.your_offer?.credits ?? listing?.Credits ?? 0) || 0;

  const yourRequests =
    payload?.comparison?.your_offer?.requests_total ??
    payload?.comparison?.your_offer?.bookings_total ??
    0;

  const creditsPercentile = payload?.comparison?.credits_percentile ?? null;
  const requestsPercentile =
    payload?.comparison?.requests_percentile ??
    payload?.comparison?.bookings_percentile ??
    null;

  const media = payload?.media || [];
  const mediaSummary = payload?.media_summary || {
    total: media.length,
    images: media.filter((m) => m.type !== 'video').length,
    videos: media.filter((m) => m.type === 'video').length,
  };

  // Heatmap: convert counts -> %
  const heatmap = payload?.industry?.heatmap || null;

  const heatmapCellsPct = useMemo(() => {
    if (!heatmap?.cells || !Array.isArray(heatmap.cells)) return [];
    const total = heatmap.cells.reduce((s, c) => s + (Number(c.count) || 0), 0);
    return heatmap.cells.map((c) => {
      const cnt = Number(c.count) || 0;
      const pct = total > 0 ? (cnt / total) * 100 : 0;
      return { ...c, pct: Math.round(pct * 10) / 10 };
    });
  }, [heatmap]);

  const heatmapMaxPct = useMemo(() => {
    if (!heatmapCellsPct.length) return 0;
    return heatmapCellsPct.reduce((m, c) => Math.max(m, Number(c.pct) || 0), 0);
  }, [heatmapCellsPct]);

  const priceBuckets = heatmap?.price_buckets || [];
  const timeBuckets = heatmap?.time_buckets || [];

  const getCellPct = (priceKey, timeKey) => {
    const cell = heatmapCellsPct.find((x) => x.priceKey === priceKey && x.timeKey === timeKey);
    return cell ? Number(cell.pct) || 0 : 0;
  };

  // Distributions (already has %)
  const priceDist = payload?.industry?.distributions?.price_bucket_share || [];
  const timeDist = payload?.industry?.distributions?.time_bucket_share || [];

  const topPriceBucket = useMemo(() => {
    if (!priceDist.length) return null;
    return [...priceDist].sort((a, b) => (b.pct || 0) - (a.pct || 0))[0];
  }, [priceDist]);

  const topTimeBucket = useMemo(() => {
    if (!timeDist.length) return null;
    return [...timeDist].sort((a, b) => (b.pct || 0) - (a.pct || 0))[0];
  }, [timeDist]);

  // Advice blocks
  const creditsPositionText = percentileToPlain(creditsPercentile);
  const requestsPositionText = percentileToPlain(requestsPercentile);

  const creditsAdvice = useMemo(() => {
    if (creditsPercentile === null || creditsPercentile === undefined) {
      return 'Not enough market data to rank your value yet. Once more venues in this category get requests, the ranking becomes meaningful.';
    }
    const p = Number(creditsPercentile);
    if (p < 30) {
      return 'You are low vs market value. This can still win if your offer is extremely easy to understand and extremely “worth it” to the right creator type — but usually you should increase perceived value or improve framing.';
    }
    if (p > 75) {
      return 'You are high vs market value. That’s strong — but it also raises expectations. To convert, you must reduce friction and sharpen creator-fit (clear brief + clear deliverables + clear timing).';
    }
    return 'You are mid-pack on value. The fastest lever here is framing: simplify the offer, show “what creators will capture”, and add a clear angle (hook) to your listing.';
  }, [creditsPercentile]);

  const requestsAdvice = useMemo(() => {
    if (requestsPercentile === null || requestsPercentile === undefined) {
      return 'Not enough category request data to rank you. Focus on the basics: offer clarity + creator-fit + timing strategy.';
    }
    const p = Number(requestsPercentile);
    if (p < 30) {
      return 'Low request pull vs market. Your opportunity is almost always: improve the listing (clarity + angle), reduce friction (fast response), and target creators whose audience matches your venue.';
    }
    if (p > 75) {
      return 'High request pull vs market. Your next lever is quality control: tighten creator criteria, strengthen brief, and standardise in-venue capture to maximise marketing output from each visit.';
    }
    return 'Mid request pull. You’ll get a step-change by improving creative direction (content hooks), then pitching a narrow set of creator types rather than “everyone”.';
  }, [requestsPercentile]);

  const heatmapHowTo = `How to use this heatmap:
• Darker cells = a higher share of MARKET booking requests.
• Use it to choose when to release / promote your offer.
• If your category is weekend-heavy, schedule your availability and creator outreach accordingly.
• If your strongest cell is “weekday dinner”, push creators mid-week and optimise reply speed.`;

  const marketVsYourExplainer = `This page separates MARKET data vs YOUR data:
• MARKET data = aggregated request behaviour across live Collabbay listings (category-filtered when possible).
• YOUR data = your offer value + your request pull (based on your current live listing).
Use market patterns to pick timing & value positioning, then use your percentile to decide what to change next.`;

  const handleRevealAI = async () => {
    setAiErr(null);

    // If already loaded, just re-show (avoid re-calling)
    if (aiPlan) {
      showTip('AI strategy already generated', 'Your custom plan is already loaded below.');
      return;
    }

    setAiLoading(true);
    try {
      const res = await fetch('https://collabbayadmin.com/APIControllers/GetBrandAIDataAnalysis.php?ai=1', {
        credentials: 'include',
      });
      const data = await res.json();
      if (!data || data.ok === false) {
        setAiErr(data?.error || 'AI request failed.');
        setAiPlan(null);
      } else {
        const plan = data?.ai?.plan || null;
        const err = data?.ai?.error || null;
        if (plan) {
          setAiPlan(plan);
          setAiErr(null);
        } else {
          setAiPlan(null);
          setAiErr(err || 'AI returned no plan.');
        }
      }
    } catch (e) {
      console.error(e);
      setAiErr('Network error generating AI plan.');
      setAiPlan(null);
    } finally {
      setAiLoading(false);
    }
  };

  // ⏳ LOADING STATE — shell but no sidenav
  if (loading) {
    return (
      <View style={styles.root}>
        <SafeAreaView style={styles.safeArea}>
          <View style={styles.shellRow}>
            <View style={styles.mainArea}>
              <View style={[styles.loadingCenter, { flex: 1 }]}>
                <ActivityIndicator size="large" color="#111827" />
                <Text style={styles.loadingText}>Loading industry data…</Text>
              </View>
            </View>
          </View>
        </SafeAreaView>
      </View>
    );
  }

  // If API error
  if (apiError) {
    return (
      <View style={styles.root}>
        <SafeAreaView style={styles.safeArea}>
          <View style={styles.shellRow}>
            <View style={styles.mainArea}>
              <View style={[styles.pageInner, { paddingTop: 18 }]}>
                <TouchableOpacity style={styles.backButton} onPress={handleBackPress}>
                  <Ionicons name="arrow-back" size={20} color="black" />
                  <Text style={styles.backButtonText}>Back</Text>
                </TouchableOpacity>

                <View style={[styles.bannerCard, { marginTop: 12 }]}>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.bannerTitle}>Industry Data</Text>
                    <Text style={styles.bannerSubtitle}>
                      We couldn’t load your market intelligence right now.
                    </Text>
                  </View>
                  <Ionicons name="warning-outline" size={28} color="black" />
                </View>

                <View style={styles.sectionCard}>
                  <Text style={styles.sectionTitle}>Error</Text>
                  <Text style={styles.sectionBody}>{apiError}</Text>
                </View>
              </View>
            </View>
          </View>
        </SafeAreaView>
      </View>
    );
  }

  // MAIN RENDER — with sidenav + industry content
  return (
    <View style={styles.root}>
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.shellRow}>
          {/* SIDE NAV */}
          <View style={styles.sideNav}>
            <LinearGradient colors={['#050509', '#050505', '#000000']} style={styles.sideNavInner}>
              {/* subtle glow blob */}
              <LinearGradient
                colors={['rgba(253,240,93,0.08)', 'rgba(0,0,0,0)']}
                style={styles.sideNavGlow}
                start={{ x: 0.1, y: 0 }}
                end={{ x: 1, y: 1 }}
              />

              {/* TOP: Logo + wordmark */}
              <View style={styles.sideNavHeader}>
                <Image
                  source={{
                    uri: 'https://collabbayadmin.com/CollabbayLogoDark.png',
                  }}
                  style={styles.sideNavLogoImage}
                  resizeMode="contain"
                />
                <View>
                  <Text style={styles.logoWordmark}>
                    <Text style={{ fontWeight: '300' }}>collab</Text>
                    bay
                    <Text style={styles.logoDot}>.</Text>
                  </Text>
                  <Text style={styles.logoDesktopTag}>DESKTOP</Text>
                </View>
              </View>

              {/* MAIN NAV SECTION */}
              <View style={styles.sideNavSection}>
                {/* Username pill */}
                <LinearGradient
                  colors={['#111111', '#2b2b2b']}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 0 }}
                  style={styles.usernamePill}
                >
                  <Text numberOfLines={1} ellipsizeMode="tail" style={styles.usernamePillText}>
                    {userData.Username ? ` ${userData.Username}` : 'Welcome Back'}
                  </Text>
                </LinearGradient>

                {/* Home → MyPanel */}
                <TouchableOpacity style={styles.navItem} onPress={() => navigation.navigate('MyPanel')}>
                  <View style={styles.navItemRow}>
                    <View style={styles.navItemMainLabel}>
                      <Ionicons
                        name="home-outline"
                        size={20}
                        color="#f5f5f5"
                        style={styles.navIcon}
                      />
                      <Text style={styles.navText}>Home</Text>
                    </View>
                  </View>
                </TouchableOpacity>

                {/* My Offers → MyOffers */}
                <TouchableOpacity style={styles.navItem} onPress={() => navigation.navigate('MyOffers')}>
                  <View style={styles.navItemRow}>
                    <View style={styles.navItemMainLabel}>
                      <Ionicons
                        name="pricetags-outline"
                        size={20}
                        color="#f5f5f5"
                        style={styles.navIcon}
                      />
                      <Text style={styles.navText}>My Offers</Text>
                    </View>
                  </View>
                </TouchableOpacity>

                {/* My Calendar → MyCalendar (LOCKED if NOT authed) */}
                <TouchableOpacity
                  style={styles.navItem}
                  onPress={() => {
                    if (authChecked && !isAuthed) {
                      openInSameTab('https://collabbayadmin.com/subscribe');
                    } else {
                      navigation.navigate('MyCalendar');
                    }
                  }}
                >
                  <View style={styles.navItemRow}>
                    <View style={styles.navItemMainLabel}>
                      <Ionicons
                        name="calendar-outline"
                        size={20}
                        color="#f5f5f5"
                        style={styles.navIcon}
                      />
                      <Text style={styles.navText}>My Calendar</Text>
                    </View>
                    {authChecked && !isAuthed && (
                      <Ionicons
                        name="lock-closed-outline"
                        size={16}
                        color="#FDF05D"
                        style={styles.lockIcon}
                      />
                    )}
                  </View>
                </TouchableOpacity>

                {/* Browse Creators → BrowseCreators */}
                <TouchableOpacity style={styles.navItem} onPress={() => navigation.navigate('BrowseCreators')}>
                  <View style={styles.navItemRow}>
                    <View style={styles.navItemMainLabel}>
                      <Ionicons
                        name="people-outline"
                        size={20}
                        color="#f5f5f5"
                        style={styles.navIcon}
                      />
                      <Text style={styles.navText}>Browse Creators</Text>
                    </View>
                  </View>
                </TouchableOpacity>

                {/* Activity → ActivityFeed (LOCKED if NOT authed) */}
                <TouchableOpacity
                  style={styles.navItem}
                  onPress={() => {
                    if (authChecked && !isAuthed) {
                      openInSameTab('https://collabbayadmin.com/subscribe');
                    } else {
                      navigation.navigate('MyActivityFeed');
                    }
                  }}
                >
                  <View style={styles.navItemRow}>
                    <View style={styles.navItemMainLabel}>
                      <Ionicons
                        name="chatbubbles-outline"
                        size={20}
                        color="#f5f5f5"
                        style={styles.navIcon}
                      />
                      <Text style={styles.navText}>Activity</Text>
                    </View>
                    {authChecked && !isAuthed && (
                      <Ionicons
                        name="lock-closed-outline"
                        size={16}
                        color="#FDF05D"
                        style={styles.lockIcon}
                      />
                    )}
                  </View>
                </TouchableOpacity>

                {/* Account → BusinessAccountPage */}
                <TouchableOpacity style={styles.navItem} onPress={() => navigation.navigate('BusinessAccountPage')}>
                  <View style={styles.navItemRow}>
                    <View style={styles.navItemMainLabel}>
                      <Ionicons
                        name="person-circle-outline"
                        size={20}
                        color="#f5f5f5"
                        style={styles.navIcon}
                      />
                      <Text style={styles.navText}>Account</Text>
                    </View>
                  </View>
                </TouchableOpacity>
              </View>

              {/* MIDDLE SECTION: Peer Map + Subscribe CTA */}
              <View style={styles.sideNavSection}>
                {/* Peer Map */}
                <TouchableOpacity
                  style={styles.peerMapCard}
                  activeOpacity={0.9}
                  onPress={() => openInSameTab('/venues')}
                >
                  <LinearGradient
                    colors={['rgba(255,255,255,0.08)', 'rgba(0,0,0,0.4)']}
                    style={styles.peerMapInner}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 1 }}
                  >
                    <View style={styles.peerMapContent}>
                      <Ionicons
                        name="map-outline"
                        size={18}
                        color="#FDF05D"
                        style={{ marginRight: 8 }}
                      />
                      <View style={{ flex: 1 }}>
                        <Text style={styles.peerMapTitle}>Peer Map</Text>
                        <Text style={styles.peerMapSubtitle}>View 100+ venues on Collabbay</Text>
                      </View>
                    </View>
                  </LinearGradient>
                </TouchableOpacity>

                {/* Subscribe CTA — only if NOT subscriber */}
                {!isSubscriber && (
                  <TouchableOpacity
                    style={styles.subscribeCard}
                    activeOpacity={0.9}
                    onPress={() => openInSameTab('https://collabbayadmin.com/subscribe')}
                  >
                    <LinearGradient
                      colors={['#FDF05D', '#fbbf24']}
                      style={styles.subscribeGradient}
                      start={{ x: 0, y: 0 }}
                      end={{ x: 1, y: 1 }}
                    >
                      <View style={styles.subscribeContent}>
                        <Ionicons
                          name="rocket-outline"
                          size={20}
                          color="#000"
                          style={{ marginRight: 8 }}
                        />
                        <View style={{ flex: 1 }}>
                          <Text style={styles.subscribeTitle}>Subscribe & unlock</Text>
                          <Text style={styles.subscribeSubtitle}>
                            Unlock Activity & Calendar, plus more.
                          </Text>
                        </View>
                      </View>
                    </LinearGradient>
                  </TouchableOpacity>
                )}
              </View>

              {/* BOTTOM: FAQ + Help */}
              <View style={styles.sideNavBottom}>
                <TouchableOpacity
                  style={styles.bottomLink}
                  onPress={() => openInSameTab('https://collabbayadmin.com/FAQs')}
                >
                  <Ionicons
                    name="help-circle-outline"
                    size={18}
                    color="#f5f5f5"
                    style={{ marginRight: 8 }}
                  />
                  <View>
                    <Text style={styles.bottomLinkText}>FAQs</Text>
                    <Text style={styles.bottomLinkCaption}>Common questions answered</Text>
                  </View>
                </TouchableOpacity>

                <TouchableOpacity
                  style={styles.bottomLink}
                  onPress={() => Linking.openURL('mailto:info@collab-bay.com')}
                >
                  <Ionicons
                    name="headset-outline"
                    size={18}
                    color="#f5f5f5"
                    style={{ marginRight: 8 }}
                  />
                  <View>
                    <Text style={styles.bottomLinkText}>Help</Text>
                    <Text style={styles.bottomLinkCaption}>We&apos;re here 24/7</Text>
                  </View>
                </TouchableOpacity>
              </View>
            </LinearGradient>
          </View>

          {/* MAIN AREA */}
          <View style={styles.mainArea}>
            <ScrollView
              contentContainerStyle={styles.scrollContent}
              showsVerticalScrollIndicator={false}
            >
              <View style={styles.pageInner}>
                {/* TOP NAV / HEADER */}
                <View style={styles.pageHeaderRow}>
                  <TouchableOpacity style={styles.backButton} onPress={handleBackPress}>
                    <Ionicons name="arrow-back" size={20} color="black" />
                    <Text style={styles.backButtonText}>Back to activity</Text>
                  </TouchableOpacity>

                  <View style={styles.bannerRight}>
                    <Text style={styles.smallWelcome}>
                      {userData.Username ? `Hi, ${userData.Username}` : 'Brand account'}
                    </Text>
                  </View>
                </View>

                <View style={styles.bannerCard}>
                  <View style={{ flex: 1, paddingRight: 12 }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                      <Text style={styles.bannerTitle}>Industry Data</Text>
                      <TouchableOpacity
                        onPress={() => showTip('What is this page?', marketVsYourExplainer)}
                        style={{ marginLeft: 10 }}
                        activeOpacity={0.8}
                      >
                        <Ionicons name="information-circle-outline" size={18} color="#111" />
                      </TouchableOpacity>
                    </View>
                    <Text style={styles.bannerSubtitle}>
                      Market intelligence for your category — built from live Collabbay <Text style={{ fontWeight: '700' }}>booking request</Text> behaviour.
                      Tap any info icon to reveal context and what to do next.
                    </Text>
                  </View>
                  <Ionicons name="analytics-outline" size={28} color="black" />
                </View>

                {/* TOP KPI STRIP (market vs your) */}
                <View style={styles.kpiRow}>
                  <View style={styles.kpiCard}>
                    <View style={styles.kpiTop}>
                      <Text style={styles.kpiLabel}>Market requests analysed</Text>
                      <TouchableOpacity
                        onPress={() =>
                          showTip(
                            'Market requests analysed',
                            'This is the total number of Collabbay booking requests (collabs rows) used to build the distributions on this page. More market volume = more reliable insight.'
                          )
                        }
                      >
                        <Ionicons name="information-circle-outline" size={16} color="#6b7280" />
                      </TouchableOpacity>
                    </View>
                    <Text style={styles.kpiValue}>{marketTotalRequests}</Text>
                    <Text style={styles.kpiSub}>MARKET data</Text>
                  </View>

                  <View style={styles.kpiCard}>
                    <View style={styles.kpiTop}>
                      <Text style={styles.kpiLabel}>Your offer value</Text>
                      <TouchableOpacity
                        onPress={() =>
                          showTip(
                            'Your offer value',
                            'This is your current live listing Credits value. Higher value can increase requests, but only if the listing is clear and the creator-fit is correct.'
                          )
                        }
                      >
                        <Ionicons name="information-circle-outline" size={16} color="#6b7280" />
                      </TouchableOpacity>
                    </View>
                    <Text style={styles.kpiValue}>£{yourCredits}</Text>

                  </View>

                  <View style={styles.kpiCard}>
                    <View style={styles.kpiTop}>
                      <Text style={styles.kpiLabel}>Your request score</Text>
                      <TouchableOpacity
                        onPress={() =>
                          showTip(
                            'Your request pull',
                            'This is how many booking requests your current live listing has received (count of collabs rows for the listing). It is not the same as redeemed bookings.'
                          )
                        }
                      >
                        <Ionicons name="information-circle-outline" size={16} color="#6b7280" />
                      </TouchableOpacity>
                    </View>
                    <Text style={styles.kpiValue}>{yourRequests}</Text>

                  </View>
                </View>

                {/* AI STRATEGY REVEAL */}
                <View style={styles.sectionCard}>
                  <View style={styles.sectionHeaderRow}>
                    <View style={{ flex: 1 }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap' }}>
  <Text style={styles.sectionTitle}>Reveal my custom AI marketing strategy…</Text>
  <View style={{ marginLeft: 8, backgroundColor: '#FDF05D', paddingHorizontal: 8, paddingVertical: 3, borderRadius: 999, borderWidth: 1, borderColor: '#000' }}>
    <Text style={{ fontSize: 10, fontWeight: '800', color: '#000', letterSpacing: 1 }}>BETA</Text>
  </View>
</View>

                      <Text style={styles.sectionSubtitle}>
                        Built from your offer, our proprietary market data, and your existing content bank.
                        Click Reveal for a bespoke marketing plan.
                      </Text>
                    </View>

                    <TouchableOpacity
                      style={[styles.revealBtn, aiLoading && { opacity: 0.75 }]}
                      onPress={handleRevealAI}
                      disabled={aiLoading}
                      activeOpacity={0.85}
                    >
                      {aiLoading ? (
                        <ActivityIndicator size="small" color="#000" />
                      ) : (
                   <Ionicons name="analytics-outline" size={16} color="#000" />

                      )}
                      <Text style={styles.revealBtnText}>
                        {aiPlan ? 'Revealed' : 'Reveal'}
                      </Text>
                    </TouchableOpacity>
                  </View>

                  {aiErr ? (
                    <View style={styles.aiErrorBox}>
                      <Ionicons name="alert-circle-outline" size={18} color="#111" style={{ marginRight: 8 }} />
                      <Text style={styles.aiErrorText}>{aiErr}</Text>
                    </View>
                  ) : null}

                  {aiPlan ? (
                    <View style={styles.aiPlanBox}>
                      <Text style={styles.aiPlanText}>{aiPlan}</Text>
                    </View>
                  ) : (
                    <View style={styles.aiLockedHint}>
                      <Text style={styles.sectionBody}>
                        Tip: After you reveal, save the plan and apply one change per day for 14 days.
                        This page is designed to inform (market patterns) and advise (what you should do next).
                      </Text>
                    </View>
                  )}
                </View>

                {/* HEATMAP */}
                <View style={styles.sectionCard}>
                  <View style={styles.sectionHeaderRow}>
                    <Text style={styles.sectionTitle}>Booking request heatmap</Text>
                    <TouchableOpacity
                      onPress={() =>
                        showTip(
                          'Heatmap',
                          `${heatmapHowTo}\n\nThis shows MARKET behaviour (not your venue alone).`
                        )
                      }
                      activeOpacity={0.8}
                    >
                      <Ionicons name="information-circle-outline" size={18} color="#6b7280" />
                    </TouchableOpacity>
                  </View>

                  <Text style={styles.sectionSubtitle}>
                    MARKET data: Where requests cluster by offer value range and time.
                  </Text>

                  <View style={styles.subHintBox}>
                    <Ionicons name="bulb-outline" size={16} color="#111" style={{ marginRight: 8 }} />
                    <Text style={styles.subHintText}>
                      Strategy: aim to push your offer to be available in the <Text style={{ fontWeight: '800' }}>darkest cells</Text>.
                      If your creators struggle to pick dates, offer a narrow “best window” aligned with the strongest cell.
                    </Text>
                  </View>

                  {/* Heatmap grid */}
                  <View style={styles.heatmapWrap}>
                    {/* Header row (time buckets) */}
                    <View style={styles.heatmapRow}>
                      <View style={[styles.heatmapCorner, { borderRightWidth: 0 }]} />
                      {timeBuckets.map((tb) => (
                        <View key={tb.key} style={styles.heatmapHeadCell}>
                          <Text style={styles.heatmapHeadText}>{tb.label}</Text>
                        </View>
                      ))}
                    </View>

                    {/* Rows */}
                    {priceBuckets.map((pb) => (
                      <View key={pb.key} style={styles.heatmapRow}>
                        <View style={styles.heatmapPriceCell}>
                          <Text style={styles.heatmapPriceText}>{pb.label}</Text>
                        </View>

                        {timeBuckets.map((tb) => {
                          const pct = getCellPct(pb.key, tb.key);
                          // intensity (0..1) based on max
                          const intensity = heatmapMaxPct > 0 ? clamp(pct / heatmapMaxPct, 0, 1) : 0;
                          const bg = `rgba(0,0,0,${0.06 + intensity * 0.72})`; // darker = higher share
                          const txtColor = intensity > 0.55 ? '#fff' : '#111';

                          return (
                            <TouchableOpacity
                              key={`${pb.key}|${tb.key}`}
                              style={[styles.heatmapCell, { backgroundColor: bg }]}
                              activeOpacity={0.85}
                              onPress={() => {
                                showTip(
                                  `Market share: ${pb.label} × ${tb.label}`,
                                  `This cell accounts for ${formatPct(pct)} of MARKET booking requests.\n\nUse it as a scheduling hint: align your availability and outreach with the strongest cells.\n\nThis is MARKET data (not only your venue).`
                                );
                              }}
                            >
                              <Text style={[styles.heatmapCellText, { color: txtColor }]}>
                                {formatPct(pct)}
                              </Text>
                            </TouchableOpacity>
                          );
                        })}
                      </View>
                    ))}
                  </View>
                </View>

                {/* DISTRIBUTIONS */}
                <View style={styles.twoColRow}>
                  {/* Value distribution */}
                  <View style={[styles.sectionCard, { flex: 1, marginRight: 10 }]}>
                    <View style={styles.sectionHeaderRow}>
                      <Text style={styles.sectionTitle}>Booking distribution based on offer values.</Text>
                      <TouchableOpacity
                        onPress={() =>
                          showTip(
                            'Value distribution',
                            'MARKET data. This shows what % of booking requests happen at each Credits range. Use it to decide if your offer value is under/over typical demand.'
                          )
                        }
                      >
                        <Ionicons name="information-circle-outline" size={18} color="#6b7280" />
                      </TouchableOpacity>
                    </View>
                    <Text style={styles.sectionSubtitle}>Live market data</Text>

                    {priceDist.map((b) => (
                      <TouchableOpacity
                        key={b.key}
                        style={styles.barRow}
                        activeOpacity={0.9}
                        onPress={() =>
                          showTip(
                            `${b.bucket}`,
                            `This bucket represents ${formatPct(b.pct)} of MARKET booking requests.\n\nRequests count: ${b.count}`
                          )
                        }
                      >
                        <Text style={styles.barLabel}>{b.bucket}</Text>
                        <View style={styles.barTrack}>
                          <View style={[styles.barFill, { width: `${clamp(b.pct, 0, 100)}%` }]} />
                        </View>
                        <Text style={styles.barPct}>{formatPct(b.pct)}</Text>
                      </TouchableOpacity>
                    ))}

                    {topPriceBucket ? (
                      <View style={styles.adviceBox}>
                        <Text style={styles.adviceTitle}>What this implies</Text>
                        <Text style={styles.adviceText}>
                          The market clusters most at <Text style={{ fontWeight: '800' }}>{topPriceBucket.bucket}</Text> ({formatPct(topPriceBucket.pct)} of requests).
                          If you’re far outside this, you must compensate with sharper framing and better creator fit.
                        </Text>
                      </View>
                    ) : null}
                  </View>

                  {/* Timing distribution */}
                  <View style={[styles.sectionCard, { flex: 1, marginLeft: 10 }]}>
                    <View style={styles.sectionHeaderRow}>
                      <Text style={styles.sectionTitle}>Booking distribution based on time-slot.</Text>
                      <TouchableOpacity
                        onPress={() =>
                          showTip(
                            'Timing distribution',
                            'MARKET data. This shows when booking requests happen. Use it to decide which days/time windows to prioritise when promoting your offer.'
                          )
                        }
                      >
                        <Ionicons name="information-circle-outline" size={18} color="#6b7280" />
                      </TouchableOpacity>
                    </View>
                    <Text style={styles.sectionSubtitle}>Live Market Data</Text>

                    {timeDist.map((b) => (
                      <TouchableOpacity
                        key={b.key}
                        style={styles.barRow}
                        activeOpacity={0.9}
                        onPress={() =>
                          showTip(
                            `${b.bucket}`,
                            `This window accounts for ${formatPct(b.pct)} of MARKET booking requests.\n\nRequests count: ${b.count}\n\nUse this to decide which days/times you should actively push creators into.`
                          )
                        }
                      >
                        <Text style={styles.barLabel}>{b.bucket}</Text>
                        <View style={styles.barTrack}>
                          <View style={[styles.barFill, { width: `${clamp(b.pct, 0, 100)}%` }]} />
                        </View>
                        <Text style={styles.barPct}>{formatPct(b.pct)}</Text>
                      </TouchableOpacity>
                    ))}

                    {topTimeBucket ? (
                      <View style={styles.adviceBox}>
                        <Text style={styles.adviceTitle}>Best window</Text>
                        <Text style={styles.adviceText}>
                          The highest-demand time is <Text style={{ fontWeight: '800' }}>{topTimeBucket.bucket}</Text> ({formatPct(topTimeBucket.pct)} of requests).
                          If you want more requests quickly, align outreach and availability to that window.
                        </Text>
                      </View>
                    ) : null}
                  </View>
                </View>

                {/* YOUR MARKET POSITION */}
                <View style={styles.sectionCard}>
                  <View style={styles.sectionHeaderRow}>
                    <Text style={styles.sectionTitle}>Your market position</Text>
                    <TouchableOpacity
                      onPress={() =>
                        showTip(
                          'Your market position',
                          'YOUR data compared to MARKET: we rank your offer value and request pull vs other live listings (category-filtered when possible).'
                        )
                      }
                      activeOpacity={0.8}
                    >
                      <Ionicons name="information-circle-outline" size={18} color="#6b7280" />
                    </TouchableOpacity>
                  </View>

                  <View style={styles.positionRow}>
                    <View style={styles.positionCard}>
                      <View style={styles.positionTop}>
                        <Text style={styles.positionLabel}>Your offer value</Text>
            
                      </View>
                      <Text style={styles.positionValue}>£{yourCredits}</Text>
                      <Text style={styles.positionRank}>
                        {creditsPercentile === null ? 'Not ranked yet' : `You are in the ${percentileToRankText(creditsPercentile)} for value`}
                        {category ? ` in ${category}.` : '.'}
                      </Text>
                      <Text style={styles.positionAdvice}>{creditsAdvice}</Text>
                    </View>

                    <View style={styles.positionCard}>
                      <View style={styles.positionTop}>
                        <Text style={styles.positionLabel}>Your request score</Text>
 
                      </View>
                      <Text style={styles.positionValue}>{yourRequests}</Text>
                      <Text style={styles.positionRank}>
                        {requestsPercentile === null ? 'Not ranked yet' : `You are in the ${percentileToRankText(requestsPercentile)} for requests`}
                        {category ? ` in ${category}.` : '.'}
                      </Text>
                      <Text style={styles.positionAdvice}>{requestsAdvice}</Text>
                    </View>
                  </View>

                  <View style={styles.marketNote}>
                    <Text style={styles.marketNoteTitle}>Important</Text>
                    <Text style={styles.marketNoteText}>
                      Market metrics are based on <Text style={{ fontWeight: '800' }}>booking requests</Text>.
                      Converting requests into real marketing output depends on: response speed, brief clarity, creator fit, and in-venue capture.
                    </Text>
                  </View>
                </View>

                {/* CONTENT BANK */}
                <View style={styles.sectionCard}>
                  <View style={styles.sectionHeaderRow}>
                    <Text style={styles.sectionTitle}>Your content bank</Text>
                    <TouchableOpacity
                      onPress={() =>
                        showTip(
                          'Your content bank',
                          'YOUR data. These are uploads from postuploads where BrandName = your session Username. They power stronger AI recommendations and help you spot what content angles you’re missing.'
                        )
                      }
                    >
                      <Ionicons name="information-circle-outline" size={18} color="#6b7280" />
                    </TouchableOpacity>
                  </View>

                  <Text style={styles.sectionSubtitle}>
                    YOUR data: {mediaSummary.total} uploads ({mediaSummary.images} images, {mediaSummary.videos} videos)
                  </Text>

                  {media.length === 0 ? (
                    <Text style={styles.sectionBody}>
                      No media found yet. Once creators upload, it will appear here and strengthen your AI recommendations.
                    </Text>
                  ) : (
                    <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.mediaRow}>
                      {media.slice(0, 18).map((m, idx) => {
                        const isVideo = m.type === 'video';
                        return (
                          <TouchableOpacity
                            key={`${m.url}-${idx}`}
                            style={styles.mediaTile}
                            activeOpacity={0.9}
                            onPress={() =>
                              showTip(
                                isVideo ? 'Video upload' : 'Image upload',
                                `This is part of your content bank.\n\nUse it to decide what angles are missing (e.g. creator POV, food close-ups, crowd vibe, location walk-in).\n\nURL:\n${m.url}`
                              )
                            }
                          >
                            <View style={styles.mediaTopTag}>
                              <Ionicons
                                name={isVideo ? 'videocam-outline' : 'image-outline'}
                                size={14}
                                color="#111"
                                style={{ marginRight: 6 }}
                              />
                              <Text style={styles.mediaTopTagText}>{isVideo ? 'Video' : 'Image'}</Text>
                            </View>

                            {isVideo ? (
                              <View style={styles.videoPlaceholder}>
                                <Ionicons name="play-circle-outline" size={34} color="#111" />
                                <Text style={styles.videoPlaceholderText}>Tap for details</Text>
                              </View>
                            ) : (
                              <Image source={{ uri: m.url }} style={styles.mediaImg} resizeMode="cover" />
                            )}
                          </TouchableOpacity>
                        );
                      })}
                    </ScrollView>
                  )}

                  <View style={styles.adviceBox}>
                    <Text style={styles.adviceTitle}>How to use this</Text>
                    <Text style={styles.adviceText}>
                      If you only see one style of content (e.g. only wide shots), your output will plateau.
                      Aim to diversify: <Text style={{ fontWeight: '800' }}>hook</Text> + <Text style={{ fontWeight: '800' }}>proof</Text> + <Text style={{ fontWeight: '800' }}>vibe</Text> + <Text style={{ fontWeight: '800' }}>CTA</Text>.
                      The AI plan will explicitly reference these URLs and tell you what to replicate and what’s missing.
                    </Text>
                  </View>
                </View>

                {/* FOOTER NOTE */}
                <View style={styles.footerNote}>
                  <Text style={styles.footerNoteText}>
                    This page is built to do two things: <Text style={{ fontWeight: '800' }}>inform</Text> (market patterns)
                    and <Text style={{ fontWeight: '800' }}>advise</Text> (suggestions on what to change next).
                    Your AI plan is generated only when you click Reveal, and is advisory only.
                  </Text>
                </View>
              </View>
            </ScrollView>
          </View>
        </View>
      </SafeAreaView>

      {/* TOOLTIP MODAL — ALWAYS ON TOP */}
      <Modal visible={tipOpen} transparent animationType="fade" onRequestClose={() => setTipOpen(false)}>
        <Pressable style={styles.tipOverlay} onPress={() => setTipOpen(false)}>
          <Pressable style={styles.tipCard} onPress={() => {}}>
            <View style={styles.tipTopRow}>
              <Text style={styles.tipTitle}>{tipTitle}</Text>
              <TouchableOpacity onPress={() => setTipOpen(false)} style={styles.tipCloseBtn}>
                <Ionicons name="close" size={18} color="#111" />
              </TouchableOpacity>
            </View>
            <Text style={styles.tipBody}>{tipBody}</Text>
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  // SHELL
  root: {
    flex: 1,
    backgroundColor: '#f3f4f6',
  },
  safeArea: {
    flex: 1,
  },
  shellRow: {
    flex: 1,
    flexDirection: 'row',
  },

  // MAIN AREA
  mainArea: {
    flex: 1,
    backgroundColor: '#f3f3f3',
  },
  scrollContent: {
    paddingVertical: 16,
    paddingHorizontal: 16,
    alignItems: 'center',
    alignSelf: 'center',
  },

  // LOADING
  loadingCenter: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 8,
    color: '#555',
    fontSize: 14,
  },

  // PAGE INNER
  pageInner: {
    width: '100%',
    maxWidth: 1100,
    alignSelf: 'center',
  },

  // HEADER / BANNER
  pageHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 999,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.06,
    shadowRadius: 2,
    elevation: 2,
  },
  backButtonText: {
    marginLeft: 6,
    fontSize: 13,
    color: '#333',
    fontWeight: '500',
  },
  bannerRight: {
    alignItems: 'flex-end',
  },
  smallWelcome: {
    fontSize: 12,
    color: '#555',
  },
  bannerCard: {
    backgroundColor: '#FDF05D',
    borderRadius: 16,
    padding: 16,
    marginBottom: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  bannerTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#111',
    marginBottom: 4,
  },
  bannerSubtitle: {
    fontSize: 13,
    color: '#333',
  },

  // KPI Row
  kpiRow: {
    flexDirection: 'row',
    marginBottom: 14,
  },
  kpiCard: {
    flex: 1,
    backgroundColor: '#fff',
    borderRadius: 14,
    padding: 14,
    borderWidth: 1,
    borderColor: '#eee',
    shadowColor: '#000',
    shadowOpacity: 0.05,
    shadowRadius: 3,
    shadowOffset: { width: 0, height: 1 },
    elevation: 2,
    marginRight: 10,
  },
  kpiTop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  kpiLabel: {
    fontSize: 12,
    color: '#6b7280',
    fontWeight: '600',
  },
  kpiValue: {
    fontSize: 20,
    fontWeight: '800',
    color: '#111',
  },
  kpiSub: {
    marginTop: 6,
    fontSize: 10,
    letterSpacing: 1.2,
    color: '#9ca3af',
    textTransform: 'uppercase',
    fontWeight: '700',
  },

  // Section Card
  sectionCard: {
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 16,
    marginBottom: 14,
    borderWidth: 1,
    borderColor: '#eee',
    shadowColor: '#000',
    shadowOpacity: 0.05,
    shadowRadius: 3,
    shadowOffset: { width: 0, height: 1 },
    elevation: 2,
  },
  sectionHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: '800',
    color: '#111',
  },
  sectionSubtitle: {
    fontSize: 12,
    color: '#6b7280',
    marginBottom: 10,
  },
  sectionBody: {
    fontSize: 13,
    color: '#333',
    lineHeight: 18,
  },

  // Reveal button
  revealBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FDF05D',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: '#111',
  },
  revealBtnText: {
    marginLeft: 6,
    fontSize: 12,
    color: '#111',
    fontWeight: '800',
  },
  aiErrorBox: {
    marginTop: 10,
    backgroundColor: '#fff3cd',
    borderWidth: 1,
    borderColor: '#f1c40f',
    padding: 10,
    borderRadius: 12,
    flexDirection: 'row',
    alignItems: 'center',
  },
  aiErrorText: {
    flex: 1,
    color: '#111',
    fontSize: 12,
    fontWeight: '700',
  },
  aiPlanBox: {
    marginTop: 12,
    backgroundColor: '#0b0b0b',
    borderRadius: 14,
    padding: 14,
  },
  aiPlanText: {
    color: '#f5f5f5',
    fontSize: 12,
    lineHeight: 18,
  },
  aiLockedHint: {
    marginTop: 10,
  },

  // Heatmap
  subHintBox: {
    marginTop: 6,
    marginBottom: 10,
    backgroundColor: '#f5f5f5',
    borderRadius: 12,
    padding: 10,
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  subHintText: {
    flex: 1,
    fontSize: 12,
    color: '#111',
    lineHeight: 17,
  },
  heatmapWrap: {
    borderWidth: 1,
    borderColor: '#eee',
    borderRadius: 14,
    overflow: 'hidden',
  },
  heatmapRow: {
    flexDirection: 'row',
  },
  heatmapCorner: {
    width: 150,
    backgroundColor: '#fafafa',
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  heatmapHeadCell: {
    flex: 1,
    paddingVertical: 10,
    paddingHorizontal: 10,
    backgroundColor: '#fafafa',
    borderLeftWidth: 1,
    borderLeftColor: '#eee',
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
    justifyContent: 'center',
  },
  heatmapHeadText: {
    fontSize: 11,
    color: '#111',
    fontWeight: '700',
    textAlign: 'center',
  },
  heatmapPriceCell: {
    width: 150,
    paddingVertical: 12,
    paddingHorizontal: 10,
    backgroundColor: '#fff',
    borderRightWidth: 1,
    borderRightColor: '#eee',
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
    justifyContent: 'center',
  },
  heatmapPriceText: {
    fontSize: 12,
    color: '#111',
    fontWeight: '800',
  },
  heatmapCell: {
    flex: 1,
    paddingVertical: 14,
    paddingHorizontal: 8,
    borderLeftWidth: 1,
    borderLeftColor: '#eee',
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
    justifyContent: 'center',
    alignItems: 'center',
  },
  heatmapCellText: {
    fontSize: 12,
    fontWeight: '900',
  },

  // Two col
  twoColRow: {
    flexDirection: 'row',
    marginBottom: 6,
  },

  // Bars
  barRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
  },
  barLabel: {
    width: 110,
    fontSize: 12,
    color: '#111',
    fontWeight: '700',
  },
  barTrack: {
    flex: 1,
    height: 10,
    backgroundColor: '#f3f3f3',
    borderRadius: 999,
    overflow: 'hidden',
    marginHorizontal: 10,
    borderWidth: 1,
    borderColor: '#eee',
  },
  barFill: {
    height: '100%',
    backgroundColor: '#111',
    borderRadius: 999,
  },
  barPct: {
    width: 54,
    textAlign: 'right',
    fontSize: 12,
    color: '#111',
    fontWeight: '800',
  },

  // Advice
  adviceBox: {
    marginTop: 10,
    backgroundColor: '#f5f5f5',
    borderRadius: 12,
    padding: 12,
  },
  adviceTitle: {
    fontSize: 12,
    fontWeight: '900',
    color: '#111',
    marginBottom: 6,
  },
  adviceText: {
    fontSize: 12,
    color: '#111',
    lineHeight: 17,
  },

  // Position cards
  positionRow: {
    flexDirection: 'row',
  },
  positionCard: {
    flex: 1,
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#eee',
    borderRadius: 14,
    padding: 14,
    marginRight: 10,
  },
  positionTop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  positionLabel: {
    fontSize: 12,
    fontWeight: '800',
    color: '#111',
  },
  pillYour: {
    fontSize: 10,
    fontWeight: '900',
    color: '#111',
    backgroundColor: '#FDF05D',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: '#111',
    overflow: 'hidden',
  },
  positionValue: {
    fontSize: 22,
    fontWeight: '900',
    color: '#111',
    marginBottom: 6,
  },
  positionRank: {
    fontSize: 12,
    color: '#111',
    fontWeight: '800',
    marginBottom: 8,
  },
  positionAdvice: {
    fontSize: 12,
    color: '#333',
    lineHeight: 17,
  },
  marketNote: {
    marginTop: 12,
    backgroundColor: '#0b0b0b',
    borderRadius: 14,
    padding: 12,
  },
  marketNoteTitle: {
    fontSize: 12,
    fontWeight: '900',
    color: '#FDF05D',
    marginBottom: 6,
  },
  marketNoteText: {
    fontSize: 12,
    color: '#f5f5f5',
    lineHeight: 17,
  },

  // Media
  mediaRow: {
    paddingVertical: 6,
    paddingRight: 8,
  },
  mediaTile: {
    width: 200,
    height: 140,
    borderRadius: 14,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#eee',
    marginRight: 10,
    backgroundColor: '#fafafa',
  },
  mediaTopTag: {
    position: 'absolute',
    top: 8,
    left: 8,
    zIndex: 2,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(253,240,93,0.95)',
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: '#111',
  },
  mediaTopTagText: {
    fontSize: 11,
    color: '#111',
    fontWeight: '900',
  },
  mediaImg: {
    width: '100%',
    height: '100%',
  },
  videoPlaceholder: {
    width: '100%',
    height: '100%',
    backgroundColor: '#f3f3f3',
    justifyContent: 'center',
    alignItems: 'center',
  },
  videoPlaceholderText: {
    marginTop: 6,
    fontSize: 11,
    fontWeight: '800',
    color: '#111',
  },

  // Footer note
  footerNote: {
    marginTop: 2,
    marginBottom: 30,
    paddingHorizontal: 6,
  },
  footerNoteText: {
    fontSize: 12,
    color: '#555',
    lineHeight: 18,
    textAlign: 'center',
  },

  // Tooltip modal (top-most)
  tipOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.45)',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 16,
  },
  tipCard: {
    width: '100%',
    maxWidth: 520,
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 14,
    borderWidth: 1,
    borderColor: '#eee',
  },
  tipTopRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  tipTitle: {
    flex: 1,
    fontSize: 14,
    fontWeight: '900',
    color: '#111',
    paddingRight: 10,
  },
  tipCloseBtn: {
    width: 32,
    height: 32,
    borderRadius: 999,
    backgroundColor: '#f3f3f3',
    alignItems: 'center',
    justifyContent: 'center',
  },
  tipBody: {
    fontSize: 12,
    color: '#111',
    lineHeight: 18,
  },

  // SIDE NAV (UNCHANGED)
  sideNav: {
    width: 250,
    backgroundColor: '#000',
  },
  sideNavInner: {
    flex: 1,
    paddingVertical: 24,
    paddingHorizontal: 16,
    borderTopRightRadius: 20,
    borderBottomRightRadius: 20,
    overflow: 'hidden',
    position: 'relative',
  },
  sideNavGlow: {
    position: 'absolute',
    top: 140,
    left: -60,
    width: 260,
    height: 260,
    borderRadius: 130,
    opacity: 0.55,
  },
  sideNavHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 26,
    zIndex: 2,
  },
  sideNavLogoImage: {
    width: 38,
    height: 38,
    marginRight: 10,
  },
  logoWordmark: {
    fontSize: 16,
    color: '#f5f5f5',
    fontWeight: '600',
    letterSpacing: 0.3,
  },
  logoDot: {
    color: '#FDF05D',
    fontSize: 18,
    fontWeight: '800',
  },
  logoDesktopTag: {
    fontSize: 9,
    color: '#b3b3b3',
    letterSpacing: 2,
    marginTop: 2,
  },
  sideNavSection: {
    marginTop: 12,
    marginBottom: 20,
    zIndex: 2,
  },
  usernamePill: {
    paddingTop: 4,
    paddingBottom: 8,
    paddingHorizontal: 10,
    borderRadius: 999,
    alignSelf: 'flex-start',
    maxWidth: '100%',
    marginLeft: 1,
    marginBottom: 10,
  },
  usernamePillText: {
    fontSize: 8,
    color: '#ffffff',
    textTransform: 'uppercase',
    letterSpacing: 1,
    textAlign: 'left',
  },
  navItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 9,
    paddingHorizontal: 8,
    borderRadius: 10,
    marginBottom: 6,
  },
  navIcon: {
    marginRight: 10,
  },
  navItemRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flex: 1,
  },
  navItemMainLabel: {
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 1,
  },
  navText: {
    color: '#f5f5f5',
    fontSize: 14,
    fontWeight: '500',
  },
  lockIcon: {
    marginLeft: 6,
  },
  peerMapCard: {
    borderRadius: 14,
    overflow: 'hidden',
    marginTop: 4,
    marginBottom: 10,
  },
  peerMapInner: {
    paddingVertical: 10,
    paddingHorizontal: 10,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
  },
  peerMapContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  peerMapTitle: {
    color: '#f5f5f5',
    fontSize: 13,
    fontWeight: '600',
  },
  peerMapSubtitle: {
    color: '#b3b3b3',
    fontSize: 11,
    marginTop: 2,
  },
  subscribeCard: {
    borderRadius: 14,
    overflow: 'hidden',
    marginTop: 10,
  },
  subscribeGradient: {
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  subscribeContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  subscribeTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: '#000',
  },
  subscribeSubtitle: {
    fontSize: 11,
    color: '#1f2933',
    marginTop: 2,
  },
  sideNavBottom: {
    marginTop: 'auto',
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#181818',
    zIndex: 2,
  },
  bottomLink: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
  },
  bottomLinkText: {
    fontSize: 13,
    color: '#f5f5f5',
    fontWeight: '600',
  },
  bottomLinkCaption: {
    fontSize: 11,
    color: '#b3b3b3',
    marginTop: 2,
  },
});

export default BrandIndustryData;
